import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faTimes } from '@fortawesome/pro-regular-svg-icons';

const EnumManagementModal = NiceModal.create(({ row }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					enumName: yup.string().nullable().required(),
					enumType: yup.string().nullable().required(),
					value: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const handleCloseModal = () => {
		modal.remove();
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: row
			? {
					...row,
					enumType: row.enumType === 'Static Enum' ? '1' : row.enumType === 'SQL Enum' ? '2' : null
				}
			: {
					enumName: null,
					enumType: null,
					value: null
				}
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		const URL = row ? ADMIN.ENUM.UPDATE_ENUM : ADMIN.ENUM.ADD_ENUM;

		await axiosPrivate
			.post(URL, {
				ID: row?.ID,
				...data
			})
			.then((response) => {
				const { status } = response;

				if (status !== 200) {
					showError();
					return;
				}

				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.ENUM_MANAGEMENT));
				handleCloseModal();
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	return (
		<Modal fullwidth maxWidth="xx-large" className="modal-enumManagementModal">
			<ModalTitle>
				<Typography variant="p1">{row ? 'updateEnum' : 'addEnum'}</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem col="6" label="name" required>
						<StringInput name="enumName" />
					</FormItem>
					<FormItem col="6" label="type" required>
						<EnumSelect
							data={['1', '2']}
							name="enumType"
							value={watch('enumType')}
							setValue={(value) => {
								setValue('enumType', value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							valueRemovable={false}
							searchable={false}
							labelPrefix="enumType."
							forcePrefix
						/>
					</FormItem>
					<FormItem label="value" required>
						<TextArea name="value" maxlength="4096" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button icon={faTimes} variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label={row ? 'update' : 'add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default EnumManagementModal;
